import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Avatar, Section, useAsync } from './ui';
import { cssBackground } from './ColorField';

/**
 * "Related …" strips for a detail page (design/023). Fetches the source
 * entity's cross-entity associations and renders up to three horizontal
 * strips (one per non-source kind, only when there are items).
 *
 * @param {object} props
 * @param {'course'|'practice'|'group'|'event'} props.sourceKind
 * @param {string} props.sourceId
 */
const KIND_TITLE = {
  course: 'Related Courses',
  practice: 'Related Practices',
  group: 'Related Groups',
  event: 'Related Events',
};

const HREF = {
  course:   (id) => `/learn/course/${id}`,
  practice: (id) => `/learn/practice/${id}`,
  group:    (id) => `/group/${id}`,
  event:    (id) => `/event/${id}`,
};

export default function RelatedStrips({ sourceKind, sourceId }) {
  const { loading, error, data } = useAsync(
    () => api.related(sourceKind, sourceId).catch(() => ({})),
    [sourceKind, sourceId],
  );
  if (loading || error || !data) return null;

  // Render in a consistent order regardless of what the server returned first.
  const kinds = ['course', 'practice', 'group', 'event'].filter((k) => k !== sourceKind);
  const anyItems = kinds.some((k) => Array.isArray(data[k]) && data[k].length > 0);
  if (!anyItems) return null;

  return (
    <>
      {kinds.map((k) => {
        const items = Array.isArray(data[k]) ? data[k] : [];
        if (items.length === 0) return null;
        return <StripSection key={k} kind={k} items={items} />;
      })}
    </>
  );
}

function StripSection({ kind, items }) {
  const navigate = useNavigate();
  return (
    <Section title={KIND_TITLE[kind]}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {items.map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(HREF[kind](r.id))}
            className="btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', background: 'var(--card-soft)',
              border: '1px solid var(--border)', borderRadius: 10,
              cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
              color: 'var(--ink)',
            }}
          >
            {/* The program's own cover / icon; else its color with the initial. */}
            <Avatar name={r.label || '?'} colorSeed={r.id} size={28} square
              src={r.imageUrl ? api.iconUrl(r.imageUrl) : null}
              bg={r.color ? cssBackground(r.color) : undefined} />
            {r.label || '(untitled)'}
          </button>
        ))}
      </div>
    </Section>
  );
}
